import type { Channel } from '@b1links/core';

declare global {
  interface Window { b1links?: { channel: Channel; apiBase: string }; Capacitor?: { isNativePlatform?: () => boolean } }
}

/** Detect which shell is running the same SPA build. */
export function detectChannel(): Channel {
  if (window.b1links?.channel) return window.b1links.channel;           // injected by Electron preload
  if (window.Capacitor?.isNativePlatform?.()) return 'mobile';           // Capacitor
  return 'web';
}

/** API base: relative on web (same origin / Vite proxy), absolute in Electron & mobile. */
export function apiBase(): string {
  if (window.b1links?.apiBase) return window.b1links.apiBase;
  const stored = localStorage.getItem('b1links.apiBase');
  if (stored) return stored;
  if (detectChannel() === 'mobile') return 'https://www.ftzones.com/B1Links'; // public gateway; override on the login screen
  // When served from an IIS sub-path (e.g. /B1Links), the server injects <base href="/B1Links/">.
  // Prefix API calls with that sub-path so they hit /B1Links/api, not the site root /api.
  const baseHref = document.querySelector('base')?.getAttribute('href');
  if (baseHref && baseHref !== '/') return baseHref.replace(/\/$/, '');
  return '';
}

/**
 * A storage suffix unique to this portal.
 *
 * B1Links, B1Budget and B1Conso are separate IIS applications on ONE host, and localStorage is scoped by
 * origin rather than by path — so without this they would all read the same saved session and a login to one
 * would silently sign you into the others. Derived from the <base href> the server injects, so it is known
 * synchronously at module load, before any API call.
 */
export function portalKey(): string {
  const base = (document.querySelector('base')?.getAttribute('href') ?? '/').replace(/^\/|\/$/g, '');
  return base || 'root';
}

/** URL of the hosted user guide — sits next to the app at <site-root>/B1Links-Guide/ on every channel. */
export function guideUrl(): string {
  const root = apiBase().replace(/\/B1Links\/?$/i, '');
  return `${root}/B1Links-Guide/`;
}
